/**
 * Farsi (Persian) Translations
 *
 * فارسی - Persian language translations for AppBouncer SDK.
 * RTL (Right-to-Left) language.
 */

import type { Translations } from '../types';

export const fa: Translations = {
  language: 'fa',
  languageName: 'فارسی',

  common: {
    demo: 'نسخه آزمایشی',
    privacy: 'حریم خصوصی',
    terms: 'شرایط',
  },

  privacy: {
    title: 'سیاست حفظ حریم خصوصی',
    whatWeStore: 'آنچه ذخیره می‌کنیم',
    whatWeStoreDesc:
      'ما به طور موقت هندسه صورت را برای تخمین سن پردازش می‌کنیم. پس از تکمیل تأیید، هیچ تصویر یا الگوی بیومتریکی ذخیره نمی‌شود.',
    whatWeDont: 'آنچه ذخیره نمی‌کنیم',
    whatWeDontDesc:
      'ما عکس، ویدیو یا شناسه‌های بیومتریکی ذخیره نمی‌کنیم. داده‌های صورت شما در مرورگر پردازش و فوراً حذف می‌شوند.',
    whyWeUseIt: 'چرا از آن استفاده می‌کنیم',
    whyWeUseItDesc:
      'تخمین سن به وب‌سایت‌ها کمک می‌کند بدون نیاز به مدارک شخصی، مقررات محدودیت سنی را رعایت کنند.',
    readFull: 'کامل بخوانید',
    privacyPolicyLink: 'سیاست حفظ حریم خصوصی',
  },

  theme: {
    system: 'سیستم',
    light: 'روشن',
    dark: 'تاریک',
  },

  face: {
    noFace: 'صورتی شناسایی نشد',
    tooFar: 'صورت خیلی دور است',
    tooClose: 'صورت خیلی نزدیک است',
    offCenter: 'صورت در مرکز نیست',
    poorLighting: 'نور کافی نیست',
    good: 'در حال تحلیل، صبر کنید...',
    holdStill: 'در حال تحلیل، صبر کنید...',
    holdStillCountdown: 'ثابت بمانید... {count}',
    processing: 'در حال پردازش...',
    looking: 'صورتی شناسایی نشد',
    centerFace: 'صورت در مرکز نیست',
    moveCloser: 'صورت خیلی دور است',
    moveBack: 'صورت خیلی نزدیک است',
    keepInPosition: 'موقعیت را حفظ کنید',
    perfect: 'عالی',
    centerInFrame: 'صورت را در کادر قرار دهید',
    outsideOval: 'صورت خارج از بیضی است',
  },

  liveness: {
    blink: {
      title: 'پلک بزنید',
      hint: 'آهسته یک بار پلک بزنید',
    },
    smile: {
      title: 'لبخند بزنید',
      hint: 'لبخند طبیعی بزنید',
    },
    lookLeft: {
      title: 'به چپ نگاه کنید',
      hint: 'آهسته سر خود را به چپ بچرخانید',
    },
    lookRight: {
      title: 'به راست نگاه کنید',
      hint: 'آهسته سر خود را به راست بچرخانید',
    },
    openMouth: {
      title: 'دهان خود را باز کنید',
      hint: 'دهان خود را کاملاً باز کنید',
    },
    lookUp: {
      title: 'به بالا نگاه کنید',
      hint: 'سر خود را به بالا خم کنید',
    },
    lookDown: {
      title: 'به پایین نگاه کنید',
      hint: 'سر خود را به پایین خم کنید',
    },
    raiseBrows: {
      title: 'ابروها را بالا ببرید',
      hint: 'ابروهای خود را بالا ببرید',
    },
    improveLighting: {
      title: 'نور را بهبود دهید',
      hint: 'برای ادامه به محیط روشن‌تری بروید',
    },
  },

  document: {
    selectType: 'نوع مدرک خود را انتخاب کنید',
    captureFront: 'عکس روی مدرک را بگیرید',
    captureBack: 'عکس پشت مدرک را بگیرید',
    processing: 'در حال پردازش مدرک شما...',
    tips: {
      goodLighting: 'از نور کافی اطمینان حاصل کنید',
      flatSurface: 'مدرک را روی سطح صاف قرار دهید',
      allCorners: 'مطمئن شوید همه گوشه‌ها قابل مشاهده است',
      noGlare: 'از انعکاس نور و سایه پرهیز کنید',
    },
    types: {
      passport: 'پاسپورت',
      idCard: 'کارت شناسایی',
      driversLicense: 'گواهینامه رانندگی',
    },
  },

  ui: {
    continue: 'ادامه',
    next: 'بعدی',
    retry: 'تلاش مجدد',
    startOver: 'شروع مجدد',
    cancel: 'انصراف',
    success: 'بررسی سن موفق',
    failure: 'بررسی سن ناموفق',
    loading: 'در حال بارگذاری...',
    error: 'خطایی رخ داد',
    permissionRequired: 'دسترسی مورد نیاز است',
    cameraPermission: 'دسترسی دوربین لازم است',
    goBack: 'برگشت',
    switchCamera: 'تغییر دوربین',
    completeVerification: 'تکمیل',
    continueToId: 'ادامه به بررسی مدارک',
  },

  screens: {
    welcome: {
      title: 'تأیید سن +۱۸',
      subtitle: 'بررسی سن ناشناس',
      startButton: 'شروع',
      secureNote: 'امن و رمزگذاری شده',
      ageTechBy: 'فناوری سن توسط AppBouncer',
      waitingCamera: 'در انتظار دسترسی به دوربین',
      allowCamera: 'اجازه دسترسی به دوربین',
      noBiometric: 'هیچ داده بیومتریکی ذخیره نمی‌شود',
      loading: 'در حال بارگذاری...',
      loadingRequirements: 'در حال بارگذاری الزامات...',
    },
    permissions: {
      title: 'در حال بارگذاری ...',
      description: 'لطفاً هنگام درخواست، اجازه دسترسی به دوربین را بدهید',
      allowButton: 'اجازه دسترسی به دوربین',
      privacyNote: 'اطلاعات شما رمزگذاری و امن است',
      goodLighting: 'نور مناسب!',
      goodLightingHint: 'اطمینان حاصل کنید که صورت شما به خوبی روشن است',
      getReady: 'آماده شوید',
      removeCoverings: 'پوشش صورت را بردارید',
      removeGlasses: 'عینک را بردارید',
    },
    facePosition: {
      title: 'صورت خود را قرار دهید',
      hint: 'صورت را وسط بگذارید',
      step: 'مرحله',
      stepOf: 'مرحله {current} از {total}',
      lookingForFace: 'در حال جستجوی صورت...',
    },
    livenessCheck: {
      title: 'بررسی زنده بودن',
      followInstructions: 'دستورالعمل‌ها را دنبال کنید',
      preparing: 'در حال آماده‌سازی...',
      complete: 'زنده‌بودن تأیید شد!',
      actionAutoDetected: 'عملکرد به صورت خودکار شناسایی می‌شود',
      failed: 'بررسی زنده بودن ناموفق',
      failedDescription:
        'نتوانستیم زنده بودن شما را تأیید کنیم. لطفاً از نور کافی اطمینان حاصل کنید، مستقیماً به دوربین نگاه کنید و دستورالعمل‌های روی صفحه را دنبال کنید.',
      retryButton: 'تلاش مجدد بررسی زنده‌بودن',
      restartButton: 'شروع مجدد',
    },
    ageEstimation: {
      title: 'ثابت بمانید',
      analyzing: 'در حال تحلیل...',
      analyzingProgress: 'در حال تحلیل صورت {current} از {total}...',
      estimatedAge: 'سن تخمینی',
      holdStill: 'ثابت بمانید',
      verified: 'سن تأیید شد',
      verificationUncertain: 'سن نامشخص',
      estimationComplete: 'بررسی سن تکمیل شد',
      confirmedAge: 'تأیید شد {minAge}+ ساله',
      couldNotConfirm: 'نتوانستیم سن {minAge}+ را تأیید کنیم',
      ageConfirmed: 'سن تأیید شد',
      aboveMinimumAge: 'شما حداقل سن مورد نیاز را دارید',
      belowMinimumAge: 'کمتر از حداقل سن',
      documentRequired: 'مدرک شناسایی لازم است',
      uploadDocumentMessage: 'لطفاً برای تأیید سن خود یک مدرک شناسایی معتبر آپلود کنید',
      idHint: 'کارت شناسایی، پاسپورت یا گواهینامه رانندگی',
      confidenceLevels: 'سطوح اطمینان بر اساس آستانه سنی',
      confidence: 'اطمینان',
      years: 'سال',
      continueToId: 'ادامه به بررسی مدارک',
      demoComplete: 'حالت نمایشی تکمیل شد. در محیط واقعی، هدایت خواهید شد.',
      improveLighting: 'نور را بهبود دهید',
      improveLightingHint: 'برای نتایج دقیق به محیط روشن‌تری بروید',
    },
    documentSelect: {
      title: 'نوع مدرک را انتخاب کنید',
      description: 'نوع مدرک شناسایی که می‌خواهید اسکن کنید را انتخاب کنید',
      idCard: 'کارت شناسایی ملی',
      idCardHint: 'کارت شناسایی دولتی',
      passport: 'پاسپورت',
      passportHint: 'فقط صفحه عکس',
      driversLicense: 'گواهینامه رانندگی',
      driversLicenseHint: 'گواهینامه معتبر',
    },
    captureMode: {
      captureFrontSide: 'عکس روی مدرک',
      captureBackSide: 'عکس پشت مدرک',
      chooseMethod: 'روش گرفتن عکس از مدرک را انتخاب کنید',
      useCamera: 'استفاده از دوربین',
      useCameraHint: 'از مدرک خود عکس بگیرید',
      uploadFile: 'آپلود فایل',
      uploadFileHint: 'از دستگاه خود انتخاب کنید',
    },
    documentCamera: {
      frontSide: 'روی مدرک',
      backSide: 'پشت مدرک',
      positionInFrame: 'مدرک را در کادر قرار دهید',
      scanFront: 'روی مدرک را اسکن کنید',
      scanBack: 'پشت مدرک را اسکن کنید',
      keepFlat: 'مدرک را صاف و با نور کافی نگه دارید',
      capturePhoto: 'گرفتن عکس',
    },
    documentPreview: {
      frontPreview: 'پیش‌نمایش روی مدرک',
      backPreview: 'پیش‌نمایش پشت مدرک',
      lookingGood: 'عالی به نظر می‌رسد!',
      checkDetails: 'مطمئن شوید همه جزئیات به وضوح قابل مشاهده است',
      imageQuality: 'کیفیت تصویر',
      qualityGood: 'خوب',
      usePhoto: 'استفاده از این عکس',
      retakePhoto: 'گرفتن عکس مجدد',
    },
    documentCapture: {
      captureFront: 'عکس روی مدرک',
      captureBack: 'عکس پشت مدرک',
      alignDocument: 'مدرک را داخل کادر قرار دهید',
      captureButton: 'عکس بگیرید',
      retakeButton: 'عکس مجدد',
      confirmButton: 'تأیید',
      frontSide: 'روی مدرک',
      backSide: 'پشت مدرک',
      positionDocument: 'مدرک خود را در کادر قرار دهید',
      alignWithFrame: 'مدرک را با کادر هم‌راستا کنید',
      positionFrontId: 'روی کارت شناسایی را قرار دهید',
      positionBackId: 'پشت کارت شناسایی را قرار دهید',
      scanFrontId: 'روی کارت شناسایی را اسکن کنید',
      scanBackId: 'پشت کارت شناسایی را اسکن کنید',
      positionWithinFrame: 'روی مدرک را داخل کادر قرار دهید',
      flipAndScan: 'کارت را برگردانید و پشت را اسکن کنید',
      goodLight: 'نور مناسب',
      flatSurface: 'سطح صاف',
      frontCaptured: 'روی مدرک ثبت شد',
      captureIdCard: 'ثبت کارت شناسایی',
      capturePassport: 'ثبت پاسپورت',
      captureDriversLicense: 'ثبت گواهینامه رانندگی',
    },
    processing: {
      title: 'در حال پردازش',
      description: 'لطفاً صبر کنید تا پردازش انجام شود',
      analyzingDocument: 'در حال تحلیل مدرک...',
      almostDone: 'تقریباً تمام شد...',
      faceVerified: 'صورت تأیید شد',
      livenessConfirmed: 'زنده بودن تأیید شد',
      processingDocuments: 'در حال پردازش مدارک...',
    },
    success: {
      title: 'تأیید سن موفق',
      description: 'تأیید سن و زنده بودن کامل شد',
      redirecting: 'در حال انتقال...',
      summary: 'خلاصه',
      faceMatch: 'تطبیق صورت',
      liveness: 'زنده بودن',
      age: 'سن',
      idDocument: 'مدرک شناسایی',
      pass: 'قبول',
      fail: 'رد',
      valid: 'معتبر',
      notApplicable: 'قابل اعمال نیست',
      done: 'تمام',
      feedbackTitle: 'لطفاً تجربه خود را ارزیابی کنید',
      feedbackPlaceholder: 'به ما بگویید چگونه می‌توانیم بهتر شویم...',
      skip: 'رد شدن',
      submit: 'ارسال',
    },
    failure: {
      title: 'بررسی سن ناموفق',
      description: 'بررسی تکمیل نشد. لطفاً دوباره تلاش کنید.',
      tryAgainButton: 'تلاش مجدد',
      leaveButton: 'خروج',
      attemptsLeft: '{{count}} تلاش باقی‌مانده',
      noAttemptsLeft: 'تلاش دیگری باقی نمانده',
    },
    error: {
      title: 'تنظیمات مورد نیاز',
      description:
        'برخی پارامترهای ضروری در آدرس URL وجود ندارد. لطفاً با مدیر وب‌سایت تماس بگیرید.',
      missingParams: 'پارامترهای ناموجود',
      requiredParams: 'پارامترهای ضروری',
      technicalDetails: 'جزئیات فنی',
      urlMustInclude: 'آدرس URL باید شامل این پارامترها باشد:',
    },
    underage: {
      title: 'شرط سنی برآورده نشد',
      description: 'برای دسترسی به این سرویس باید حداقل {{minAge}} ساله باشید.',
    },
    consent: {
      title: 'به بهبود بررسی سن کمک کنید',
      description:
        'به AppBouncer اجازه دهید مقدار کمی داده ناشناس (مانند تصاویر ضبط شده) را برای بهبود تأیید سن جمع‌آوری کند. اطلاعات شما هرگز فروخته یا به اشخاص ثالث داده نمی‌شود.',
      allowButton: 'اجازه می‌دهم',
    },
    documentConsent: {
      title: 'مدرک مورد نیاز',
      description:
        'برای تکمیل بررسی، باید سن شما را با یک مدرک رسمی تأیید کنیم. آیا موافق با آپلود مدرک شناسایی خود هستید؟',
      allowButton: 'ادامه با مدرک',
      declineButton: 'انصراف',
      privacyNote: 'مدرک شما به صورت امن پردازش شده و پس از بررسی حذف خواهد شد.',
    },
    selfDeclaration: {
      title: 'تاریخ تولد شما',
      description: 'لطفاً تاریخ تولد خود را وارد کنید تا ادامه دهید',
      day: 'روز',
      month: 'ماه',
      year: 'سال',
      yourAge: 'سن شما:',
      years: 'سال',
      confirmButton: 'ادامه',
      months: {
        january: 'ژانویه',
        february: 'فوریه',
        march: 'مارس',
        april: 'آوریل',
        may: 'مه',
        june: 'ژوئن',
        july: 'ژوئیه',
        august: 'اوت',
        september: 'سپتامبر',
        october: 'اکتبر',
        november: 'نوامبر',
        december: 'دسامبر',
      },
    },
  },

  lighting: {
    warning: 'مشکل نور',
    adjust: 'نور را تنظیم کنید',
    lowLight: 'نور کم شناسایی شد',
    tooBright: 'بیش از حد روشن',
    poorContrast: 'کنتراست ضعیف',
    improveLighting: 'به نور بهتر بروید',
    moveToLighter: 'به محیط روشن‌تری بروید',
    videoNotReady: 'ویدیو آماده نیست',
  },

  errors: {
    cameraNotFound: 'دوربینی در این دستگاه یافت نشد',
    cameraPermissionDenied: 'دسترسی به دوربین رد شد',
    cameraInUse: 'دوربین توسط برنامه دیگری در حال استفاده است',
    cameraNotSupported: 'دوربین از تنظیمات مورد نیاز پشتیبانی نمی‌کند',
    cameraSecurityError: 'دوربین نیاز به اتصال HTTPS دارد',
    networkError: 'خطای شبکه. لطفاً اتصال خود را بررسی کنید.',
    timeout: 'درخواست منقضی شد. لطفاً دوباره تلاش کنید.',
    invalidApiKey: 'کلید API نامعتبر',
    sessionExpired: 'جلسه منقضی شد. لطفاً از نو شروع کنید.',
    verificationFailed: 'بررسی ناموفق بود. لطفاً دوباره تلاش کنید.',
    unknownError: 'خطای غیرمنتظره رخ داد',
    cameraAccessDeniedDetailed: 'لطفاً برای ادامه دسترسی دوربین را فعال کنید.',
    cameraNotFoundDetailed: 'دوربینی شناسایی نشد. لطفاً دوربین را متصل کنید و دوباره تلاش کنید.',
    cameraInUseDetailed:
      'دوربین توسط برنامه دیگری در حال استفاده است. لطفاً آن را ببندید و دوباره تلاش کنید.',
    cameraOverconstrained:
      'دوربین از رزولوشن درخواستی پشتیبانی نمی‌کند. در حال تلاش با تنظیمات پیش‌فرض...',
    networkTimeout: 'درخواست بیش از حد طول کشید. لطفاً اتصال خود را بررسی کنید و دوباره تلاش کنید.',
    networkOffline: 'به نظر می‌رسد آفلاین هستید. لطفاً اتصال اینترنت خود را بررسی کنید.',
    apiGenericError: 'مشکلی پیش آمد. لطفاً دوباره تلاش کنید.',
    faceNotDetected: 'لطفاً صورت خود را در کادر قرار دهید.',
    livenessFailed: 'بررسی زنده بودن ناموفق بود. لطفاً دوباره تلاش کنید.',
    documentCaptureFailed:
      'نتوانستیم مدرک را ثبت کنیم. لطفاً از نور کافی اطمینان حاصل کنید و دوباره تلاش کنید.',
    initializationFailed: 'بارگذاری ناموفق. لطفاً صفحه را بازنشانی کنید و دوباره تلاش کنید.',
    modelLoadFailed:
      'بارگذاری اجزای مورد نیاز ناموفق بود. لطفاً صفحه را بازخوانی کنید و دوباره تلاش کنید.',
    unexpectedError: 'خطای غیرمنتظره رخ داد. لطفاً دوباره تلاش کنید.',
    configError: 'خطای پیکربندی. لطفاً با پشتیبانی تماس بگیرید.',
  },

  steps: {
    faceDetection: 'شناسایی صورت',
    liveness: 'بررسی زنده بودن',
    age: 'بررسی سن',
    documentScan: 'اسکن مدرک',
    selfDeclaration: 'خوداظهاری',
  },

  status: {
    cameraStarted: 'دوربین روشن شد',
    cameraReady: 'دوربین آماده است',
    cameraAccessDenied: 'دسترسی به دوربین رد شد',
    cameraError: 'خطای دوربین',
    cameraNotAvailable: 'دوربین در دسترس نیست',
    noFaceDetected: 'صورتی شناسایی نشد',
    livenessVerified: 'زنده بودن تأیید شد',
    livenessCheckFailed: 'بررسی زنده بودن ناموفق بود',
    verifyingAge: 'در حال تأیید سن...',
    processingDocument: 'در حال پردازش مدرک...',
    documentProcessed: 'مدرک با موفقیت پردازش شد',
    verificationCancelled: 'بررسی لغو شد',
    improveLightingForAge: 'برای نتایج دقیق نور را بهبود دهید',
    getReady: 'آماده شوید',
    preparingLiveness: 'در حال آماده‌سازی بررسی زنده بودن...',
    uploadingSelfie: 'در حال آپلود تصاویر سلفی...',
    preparingUpload: 'در حال آماده‌سازی آپلود...',
    uploadingFront: 'در حال آپلود مدرک جلویی...',
    uploadingBack: 'در حال آپلود مدرک پشتی...',
    processingDocuments: 'در حال پردازش مدارک...',
    documentSubmissionFailed: 'ارسال مدرک ناموفق بود. لطفاً دوباره تلاش کنید.',
    selectJpegOrPng: 'لطفاً یک تصویر JPEG یا PNG انتخاب کنید',
    fileTooLarge: 'فایل بسیار بزرگ است. حداکثر ۵ مگابایت مجاز است.',
    errorReadingFile: 'خطا در خواندن فایل',
    frontDocumentRequired: 'تصویر روی مدرک الزامی است',
    verificationFailedClose: 'بررسی ناموفق بود. به صفحه قبل هدایت خواهید شد.',
    verificationCompleteClose: 'بررسی تکمیل شد! به صفحه قبل هدایت خواهید شد.',
    analyzingFace: 'در حال تحلیل صورت...',
  },
};
